import { useState } from 'react';
import { Calendar, Info, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Card, CardContent } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';

interface OnboardingSlideProps {
  onComplete: () => void;
  isCompleting: boolean;
  isLastSlide: boolean;
  onNext?: () => void;
  onGenderSelect?: (gender: string) => void;
}

const GENDER_OPTIONS = [
  { value: 'woman', label: 'Woman' },
  { value: 'man', label: 'Man' },
  { value: 'non-binary', label: 'Non-binary' },
  { value: 'self-describe', label: 'Prefer to self-describe' },
  { value: 'prefer-not-to-say', label: 'Prefer not to say' },
];

export const OnboardingSlidePersonal = ({
  onNext,
  onGenderSelect
}: OnboardingSlideProps) => {
  const [birthYear, setBirthYear] = useState('');
  const [gender, setGender] = useState('');
  const [customGender, setCustomGender] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  
  const currentYear = new Date().getFullYear();
  
  const handleContinue = async () => {
    if (!birthYear) {
      toast.error('Please enter your year of birth');
      return;
    }
    const yearNum = parseInt(birthYear);
    if (isNaN(yearNum) || yearNum < 1900 || yearNum > currentYear) {
      toast.error('Please enter a valid year of birth');
      return;
    }
    
    const age = currentYear - yearNum;
    if (age < 13) {
      toast.error('You must be at least 13 years old to use FaceFable');
      return;
    }
    
    // Determine the gender value to save
    let genderToSave: string | null = null;
    if (gender === 'self-describe' && customGender.trim()) {
      genderToSave = customGender.trim();
    } else if (gender && gender !== 'self-describe') {
      genderToSave = gender;
    } else if (gender === 'self-describe') {
      genderToSave = 'self-describe';
    }
    
    setIsSaving(true);
    try {
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      if (!user) {
        // User not authenticated yet, just continue to next slide
        if (onNext) onNext();
        return;
      }

      const dateOfBirth = `${yearNum}-01-01`;
      
      // Ensure profile exists first
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', user.id)
        .maybeSingle();
      
      if (!existingProfile) {
        console.error('Profile does not exist for user');
        toast.error('Profile not found. Please try signing in again.');
        return;
      }
      
      const {
        error
      } = await supabase.from('profiles').update({
        date_of_birth: dateOfBirth,
        age: age,
        gender: genderToSave
      }).eq('id', user.id);
      
      if (error) {
        console.error('Profile update error:', error);
        throw error;
      }
      
      // Notify parent of gender selection
      if (gender && onGenderSelect) {
        onGenderSelect(gender);
      }
      
      if (onNext) onNext();
    } catch (error) {
      console.error('Error saving year of birth:', error);
      toast.error('Failed to save year of birth. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };
  
  return (
    <Card className="shadow-card border-0 bg-gradient-card">
      <CardContent className="p-6 md:p-8 flex flex-col items-center justify-center text-center">
        {/* Visual */}
        <div className="mb-6 relative">
          <div className="w-24 h-24 md:w-28 md:h-28 rounded-full bg-gradient-primary flex items-center justify-center animate-scale-in shadow-glow">
            <Calendar className="w-12 h-12 md:w-14 md:h-14 text-primary-foreground" />
          </div>
        </div>

        {/* Title */}
        <h2 className="text-xl md:text-2xl font-semibold mb-3 animate-fade-in">
          Help Us Personalise Your Journey
        </h2>

        {/* Subtext */}
        <p className="text-sm text-muted-foreground max-w-2xl mb-4 animate-fade-in" style={{
          animationDelay: '0.2s'
        }}>
          Your year of birth and gender help us provide more accurate benchmarking and personalised insights tailored to your skin's needs.
        </p>
        
        {/* Benefits Card */}
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6 max-w-md animate-fade-in" style={{
          animationDelay: '0.3s'
        }}>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-1">
              <Users className="w-5 h-5 text-primary" />
            </div>
            <div className="text-left">
              <p className="text-sm font-medium text-foreground mb-1">
                Unlock Personalised Insights
              </p>
              <p className="text-xs text-muted-foreground">
                See how your FaceScore compares to others in your age and gender group. Get your percentile ranking and personalized recommendations.
              </p>
            </div>
          </div>
        </div>

        {/* Year of Birth and Gender Inputs */}
        <div className="w-full max-w-sm mx-auto space-y-6 animate-fade-in" style={{
          animationDelay: '0.4s'
        }}>
          {/* Year of Birth */}
          <div className="space-y-2">
            <div className="flex items-center justify-center gap-2">
              <Label htmlFor="birthYear" className="text-center">Year of Birth</Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="w-4 h-4 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-md p-4" side="bottom">
                    <div className="space-y-3">
                      <p className="text-sm font-semibold">Why We Ask for Your Year of Birth</p>
                      <p className="text-xs">
                        Your skin changes naturally as you move through different stages of life — and the routine you need changes with it.
                      </p>
                      <ul className="text-xs space-y-2 list-none">
                        <li>• In your <strong>20s</strong>, skin focuses on hydration, prevention, and barrier support.</li>
                        <li>• In your <strong>30s</strong>, collagen begins to decline, so repair ingredients become more important.</li>
                        <li>• In your <strong>40s and 50s+</strong>, skin often needs deeper nourishment, firmness support, and gentler routines.</li>
                      </ul>
                      <p className="text-xs font-medium">By knowing your year of birth, FaceFable can:</p>
                      <ul className="text-xs space-y-1 list-none">
                        <li>• understand what your skin needs at this stage of life</li>
                        <li>• tailor your routine to match how skin typically behaves over time</li>
                        <li>• recommend products that are appropriate, safe, and effective</li>
                        <li>• avoid suggestions that may be too strong — or not strong enough</li>
                      </ul>
                      <p className="text-xs italic">
                        Your year of birth is never used for marketing. It simply helps FaceFable choose the right approach for your skin right now, and ensure your routine evolves as your skin does.
                      </p>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Input 
              id="birthYear" 
              type="number" 
              value={birthYear} 
              onChange={e => setBirthYear(e.target.value)} 
              min="1900" 
              max={currentYear} 
              className="text-sm h-9 text-center" 
              placeholder="e.g. 1990" 
            />
            <p className="text-xs text-muted-foreground text-center pt-1">
              We ask for your year of birth so FaceFable can tailor your routine to your skin's stage. Skin changes over time — your routine should too.
            </p>
          </div>

          {/* Gender Selection */}
          <div className="space-y-3">
            <div className="flex items-center justify-center gap-2">
              <Label className="text-center">Gender (optional)</Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="w-4 h-4 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-md p-4" side="bottom">
                    <div className="space-y-3">
                      <p className="text-sm font-semibold">Why We Ask for Your Gender</p>
                      <p className="text-xs">
                        Skin behaves differently across genders due to natural hormonal, structural, and ageing differences.
                      </p>
                      <ul className="text-xs space-y-2 list-none">
                        <li>• <strong>Hormonal factors</strong> affect oil production, hydration levels, and skin thickness.</li>
                        <li>• <strong>Structural differences</strong> influence how skin ages and responds to treatments.</li>
                        <li>• <strong>Benchmarking accuracy</strong> improves when we compare your FaceScore to similar profiles.</li>
                      </ul>
                      <p className="text-xs font-medium">By knowing your gender, FaceFable can:</p>
                      <ul className="text-xs space-y-1 list-none">
                        <li>• provide more accurate FaceScore benchmarks</li>
                        <li>• tailor product recommendations to your skin type</li>
                        <li>• offer insights relevant to your specific skin concerns</li>
                        <li>• show hormonal health options where relevant</li>
                      </ul>
                      <p className="text-xs italic">
                        Your gender is never used for marketing or shared with anyone. It only helps FaceFable give you more personalised and accurate results.
                      </p>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            
            <RadioGroup 
              value={gender} 
              onValueChange={setGender}
              className="flex flex-col gap-2"
            >
              {GENDER_OPTIONS.map((option) => (
                <div 
                  key={option.value}
                  className="flex items-center space-x-3 rounded-lg border border-border/50 p-3 hover:bg-muted/30 transition-colors cursor-pointer"
                  onClick={() => setGender(option.value)}
                >
                  <RadioGroupItem value={option.value} id={option.value} />
                  <Label 
                    htmlFor={option.value} 
                    className="text-sm font-normal cursor-pointer flex-1 text-left"
                  >
                    {option.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>

            {/* Self-describe input */}
            {gender === 'self-describe' && (
              <div className="space-y-2 pt-2 animate-fade-in">
                <Label htmlFor="customGender" className="text-sm text-left block">
                  How do you describe your gender? (optional)
                </Label>
                <Textarea
                  id="customGender"
                  value={customGender}
                  onChange={(e) => setCustomGender(e.target.value)}
                  placeholder="Your gender identity"
                  className="text-sm min-h-[60px] resize-none"
                  maxLength={100}
                />
                <p className="text-xs text-muted-foreground text-left">
                  This helps improve personalisation. Your answer is private.
                </p>
              </div>
            )}

            {/* Explanatory text */}
            <p className="text-xs text-muted-foreground text-center pt-2">
              Gender helps us improve accuracy of your FaceScore by accounting for natural hormonal, structural, and ageing differences across groups. This data is private and never shared.
            </p>
          </div>

          {/* Continue Button */}
          <div className="flex flex-col gap-2 pt-2">
            <Button 
              size="lg" 
              className="w-full bg-gradient-primary hover:opacity-90 shadow-button" 
              onClick={handleContinue} 
              disabled={isSaving || !birthYear}
            >
              {isSaving ? 'Saving...' : 'Continue'}
            </Button>
          </div>
        </div>

        {/* Privacy Note */}
        <p className="text-xs text-muted-foreground mt-4 max-w-md animate-fade-in" style={{
          animationDelay: '0.6s'
        }}>
          🔒 Your information is private and secure. We never share your personal data.
        </p>
      </CardContent>
    </Card>
  );
};
